/**
 * Candidate Stage 2 API client. All calls hit /api/candidate/stage2/* (Plan 04-03).
 *
 * SEPARATE token from Stage 1 (5.0h, A1): the Stage-2 surface authenticates with a
 * `stage_2_token` Bearer that HR mints (Plan 04-01) and pastes into the candidate's
 * video-invite link. The URL param `:stage2Token` IS that Bearer — the layout persists
 * it into sessionStorage under its OWN key (`stage_2_token`, NOT the Stage-1
 * `candidate_session_token`) so the guard + every fetch can read it. Mirrors the Phase-3
 * candidateFetch pattern (dedicated, sessionStorage Bearer, NEVER the admin JWT / apiFetch),
 * including the deliberate backend separation of the candidate-token auth subsystem from
 * admin JWT — a 401 here surfaces a localized "session expired" + RequireStage2Session
 * redirect, never an admin /login bounce.
 *
 * Reuses ApiError from @/types/api (mirrors Plan 03-05 divergence #1) so mapErrorSlug works.
 */
import { ApiError } from "@/types/api";
import type { ApiErrorBody } from "@/types/api";

const STAGE2_SESSION_KEY = "stage_2_token";

export function getStage2SessionToken(): string | null {
  return sessionStorage.getItem(STAGE2_SESSION_KEY);
}

export function setStage2SessionToken(token: string): void {
  sessionStorage.setItem(STAGE2_SESSION_KEY, token);
}

export function clearStage2SessionToken(): void {
  sessionStorage.removeItem(STAGE2_SESSION_KEY);
}

// One-way confirmation (CAN-12): persist reference_number on upload success so the
// /done screen survives a hard refresh within the tab. Mirrors the Phase-3
// candidate_reference_number pattern (Plan 03-06 D-F).
const STAGE2_REFERENCE_KEY = "stage_2_reference_number";

export function getStage2ReferenceNumber(): string | null {
  return sessionStorage.getItem(STAGE2_REFERENCE_KEY);
}

export function setStage2ReferenceNumber(ref: string): void {
  sessionStorage.setItem(STAGE2_REFERENCE_KEY, ref);
}

/**
 * Stage-2-scoped fetch. Attaches the sessionStorage `stage_2_token` Bearer when present.
 * Throws ApiError on any non-2xx (no admin-store mutation, no /login redirect) so callers
 * route the error through mapErrorSlug + sonner toast + RequireStage2Session.
 *
 * NOTE: multipart uploads do NOT go through this helper — useVideoUpload (Task 2) builds
 * its own XHR so it can report upload progress and must let the browser set the multipart
 * Content-Type + boundary. This helper is for the JSON/no-body endpoints only.
 */
export async function stage2Fetch<T>(
  path: string,
  opts: RequestInit = {},
): Promise<T> {
  const headers = new Headers(opts.headers);
  if (!headers.has("Content-Type") && opts.body !== undefined) {
    headers.set("Content-Type", "application/json");
  }
  const tok = getStage2SessionToken();
  if (tok) headers.set("Authorization", `Bearer ${tok}`);

  const res = await fetch(path, { ...opts, headers });

  if (!res.ok) {
    const body: ApiErrorBody = await res
      .json()
      .catch(() => ({ detail: "api_error" }));
    throw new ApiError(
      res.status,
      body,
      res.headers.get("x-request-id") ?? undefined,
    );
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// ---- Response/request shapes (match backend/app/schemas/candidate_stage2.py EXACTLY, BUG-017) ----

/** GET /video-consent → active Ley-9877 video consent for the position language (CAN-08). */
export interface VideoConsentResponse {
  position_id: number;
  position_language: "es" | "en" | "de";
  /** C5: HR-authored per-position video prompt (question the candidate answers). Empty when unset. */
  video_prompt: string;
  deadline: string;
  consent_text_md: string;
  consent_version_id: number;
  consent_version_number: number;
}

/** POST /video-consent/accept → stamp (no request body — backend reads candidate from token). */
export interface VideoConsentAccepted {
  video_consent_version_id: number;
  accepted_at: string;
}

/** POST /salary body + the inline salary fields on the multipart upload (A3/CAN-11). */
export interface SalaryUpdatePayload {
  salary_update: number;
  salary_currency: "CRC" | "USD";
}

/** GET /state + POST /salary → one-way confirmation state (CAN-12). */
export interface Stage2StateResponse {
  video_consent_accepted: boolean;
  video_uploaded: boolean;
  reference_number: string | null;
  deadline: string;
}

/** POST /video → upload accepted (CAN-10/11). */
export interface VideoUploadAccepted {
  status: "ok";
  reference_number: string | null;
}

// ---- Endpoints (Plan 04-03 backend) ----

export async function getVideoConsent(): Promise<VideoConsentResponse> {
  return stage2Fetch<VideoConsentResponse>(
    "/api/candidate/stage2/video-consent",
  );
}

/** Accept the Ley-9877 video consent. POST with no body (backend stamps from token). */
export async function acceptVideoConsent(): Promise<VideoConsentAccepted> {
  return stage2Fetch<VideoConsentAccepted>(
    "/api/candidate/stage2/video-consent/accept",
    { method: "POST" },
  );
}

/** Standalone Stage 2 salary revision (CAN-11). Returns the updated confirmation state. */
export async function postStage2Salary(
  payload: SalaryUpdatePayload,
): Promise<Stage2StateResponse> {
  return stage2Fetch<Stage2StateResponse>("/api/candidate/stage2/salary", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function getStage2State(): Promise<Stage2StateResponse> {
  return stage2Fetch<Stage2StateResponse>("/api/candidate/stage2/state");
}
